import { DateTime } from 'luxon';
import { GetOrder, OrderRefSearch } from 'smg/customer-api/orders';
import { DownloadOrderInvoice } from 'smg/customer-api/v2/orders';
import * as buttons from 'smg/buttons';
import { ShowError } from 'smg/errors';
import { Carriers } from 'smg/customer-api/carriers';
import * as models from 'smg/customer-api/models';
import { OpenProofFromNodes } from 'smg/fancybox';
import * as html from 'smg/html';
import QueryString from 'smg/querystring';
import { DollarFormat } from 'smg/util';

void main();

async function main() {
    function stopSpinner() {
        spinner.classList.add('d-none');
    }

    function reveal(): void {
        content.classList.remove('d-none');
    }

    // The order emails link one proof by id (?proof=). Open it in the gallery
    // so the customer lands on the artwork itself; without this the link is a
    // CloudFront file whose Content-Disposition makes the browser download it.
    function openLinkedProof($proofLinks: HTMLElement[]): void {
        const proofID = QueryString().get('proof');
        if (!proofID) {
            return;
        }

        const $target = $proofLinks.find($link => $link.getAttribute('data-proof-id') === proofID);
        if (!$target) {
            return;
        }

        OpenProofFromNodes($proofLinks, $target);
    }

    function isDelivered(deliveryCounter: number, deliveredQuantity: models.OrderShippingOptionShipment[]): void {
        if (deliveryCounter === deliveredQuantity.length) {
            orderStatus.textContent = 'Delivered';
            deliveredEl.textContent = 'Delivered';
        }

        if (deliveryCounter !== deliveredQuantity.length) {
            orderStatus.textContent = 'Partially Delivered';
            deliveredEl.textContent = 'Partially Delivered';
        }

        if (deliveredQuantity.length) {
            const lastShipment = deliveredQuantity[deliveredQuantity.length - 1].shipment;
            if (lastShipment?.deliveredAt.isValid) {
                deliveredDateEl.textContent = lastShipment.deliveredAt.toLocaleString(DateTime.DATE_SHORT);
            }
        }

        orderStatus.style.color = '#52694C';
        progressBar.classList.add('progress-delivered');

        window.setTimeout(() => {
            progressBar.style.width = '100%';
            progressBar.setAttribute('aria-valuenow', '100%');
        }, 2000);
    }

    function hasBeenShipped(shippedCounter: number, shippedQuantity: models.OrderShippingOptionShipment[]): void {
        const shippedEl = document.querySelector<HTMLElement>('.shipped') as HTMLElement;
        const shippedDateTextEl = document.querySelector<HTMLElement>('.shipped-date') as HTMLElement;

        if (!delivered) {
            if (shippedCounter === shippedQuantity.length) {
                orderStatus.textContent = 'Shipped';
            }

            if (shippedCounter !== shippedQuantity.length) {
                orderStatus.textContent = 'Partially Shipped';
            }

            progressBar.classList.add('progress-shipped');
            orderStatus.classList.add('text-primary');
            window.setTimeout(() => {
                progressBar.style.width = '75%';
                progressBar.setAttribute('aria-valuenow', '75%');
            }, 2000);
        }

        shippedEl.textContent = 'Shipped';

        if (shippedQuantity.length) {
            const lastShipment = shippedQuantity[shippedQuantity.length - 1]?.shipment;
            if (lastShipment?.shippedAt.isValid) {
                shippedDateTextEl.textContent = lastShipment.shippedAt.toLocaleString(DateTime.DATE_SHORT);
            }
            void trackingInformation(shippedQuantity);
        }
    }

    function hasGoneThroughProduction(order: models.HyejooOrder): void {
        if (!delivered && !shipped) {
            const icon = document.createElement('span');
            progressBar.classList.add('progress-in-production');
            orderStatus.textContent = 'In Production ';
            // orderStatus.style.color = `#0d6efd`;
            icon.classList.add('material-symbols-sharp', 'spin');
            icon.setAttribute('aria-hidden', 'true');
            icon.textContent = 'build';
            orderStatus.append(icon);
            window.setTimeout(() => {
                progressBar.style.width = '50%';
                progressBar.setAttribute('aria-valuenow', '50%');
            }, 2000);
        }

        const productionTextEl = document.querySelector('.in-production') as HTMLElement;
        const productionDateEl = document.querySelector('.production-date') as HTMLElement;

        productionTextEl.textContent = 'In Production';
        if (order.confirmedTime.isValid) {
            productionDateEl.textContent = order.confirmedTime.toLocaleString(DateTime.DATE_SHORT);
        }
    }

    function hasBeenReceived(order: models.HyejooOrder): void {
        if (!delivered && !shipped && !inProduction) {
            orderStatus.textContent = 'Order Received';
            progressBar.classList.add('progress-received');
            window.setTimeout(() => {
                progressBar.style.width = '25';
                progressBar.setAttribute('aria-valuenow', '25%');
            }, 2000);
        }
        const receivedDateEl = document.querySelector('.received-date') as HTMLElement;
        const receivedTextEl = document.querySelector('.received') as HTMLElement;

        if (order.created.isValid) {
            receivedDateEl.textContent = order.created.toLocaleString(DateTime.DATE_SHORT);
        }
        receivedTextEl.textContent = 'Order Received';

        if (!delivered && order.customerShipDate.isValid) {
            deliveredEl.textContent = 'Estimated Delivery';
            deliveredDateEl.textContent = order.customerShipDate.toLocaleString(DateTime.DATE_SHORT);
        }
    }

    async function trackingInformation(shippingOptionShipment: models.OrderShippingOptionShipment[]) {
        const hasTracking = document.querySelector('.has-tracking') as HTMLElement;
        const noTracking = document.querySelector('.no-tracking') as HTMLElement;
        const carriersMap = await Carriers();

        if (shippingOptionShipment.length > 1) {
            const trackingGroup = document.querySelector('.tracking-group') as HTMLElement;
            const trackingColumns = document.querySelector('.tracking-columns') as HTMLElement;

            for (const shipments of shippingOptionShipment) {
                const carrier = carriersMap.get(shipments.shipment?.carrierID ?? 0);
                const column = document.createElement('div');
                const aTag = document.createElement('a');
                aTag.href = carrier?.trackingURL(shipments.shipment?.trackingNumber || '') || '';
                aTag.innerText = `#${shipments.shipment?.trackingNumber || ''}`;
                if (shipments.shipment?.deliveredAt.isValid) {
                    aTag.style.color = '#52694C';
                }
                column.classList.add('col');
                column.append(aTag);
                trackingColumns.append(column);
            }
            trackingGroup?.classList.remove('d-none');
            hasTracking.classList.remove('d-none');
            noTracking.remove();
        }

        if (shippingOptionShipment.length === 1) {
            // This stuff for only one tracking number
            const carrier = carriersMap.get(shippingOptionShipment[0].shipment?.carrierID || 0);
            if (carrier?.link) {
                trackingLink.href = carrier.trackingURL(shippingOptionShipment[0]?.shipment?.trackingNumber || '');
            }
            trackingLink.innerText = `#${shippingOptionShipment[0].shipment?.trackingNumber || ''}`;
            hasTracking.classList.remove('d-none');
            noTracking.remove();
        }
    }

    const spinner = document.querySelector('#spinner') as HTMLDivElement;
    const content = document.querySelector('#content') as HTMLDivElement;
    const noKey = document.querySelector('#noKey') as HTMLElement;
    const delivery = document.querySelector('#deliveryInformation') as HTMLElement;
    const oderNumber = document.querySelector('.orderNumber') as HTMLElement;
    const orderDate = document.querySelector('.orderDate') as HTMLElement;
    const orderStatus = document.querySelector('.orderStatus') as HTMLElement;
    const subtotalEl = document.querySelector('.subtotal') as HTMLElement;
    const shipping = document.querySelector('.shipping') as HTMLElement;
    const salesTax = document.querySelector('.salesTax') as HTMLElement;
    const total = document.querySelector('.total') as HTMLElement;
    const progressBar = document.querySelector('.progress-bar') as HTMLElement;
    const trackingParentEl = document.querySelector('.tracking-parent') as HTMLElement;
    const trackingLink = trackingParentEl.querySelector('.tracking-link') as HTMLAnchorElement;
    const deliveredEl = document.querySelector('.delivered') as HTMLElement;
    const deliveredDateEl = document.querySelector('.delivered-date') as HTMLElement;
    const proofsContainer = document.querySelector('.proofsContainer') as HTMLElement;
    const proofsParentEl = document.querySelector('.proofs') as HTMLElement;
    let delivered = false;
    let shipped = false;
    let inProduction = false;
    // let received = false;
    let shippedCounter = 0;
    let deliveryCounter = 0;

    const key = QueryString().get('id');

    if (key) {
        try {
            const data = await GetOrder(key, {
                data: 'id,invoiceNumber,created,confirmedTime,customerShipDate,orderStatusID,total,totalSansTax,balance,orderShippingOptions,orderProofs',
            });
            const order = data;
            if (!order) {
                stopSpinner();
                noKey.classList.remove('d-none');
                return;
            }

            // Order Date
            if (order.created.isValid) {
                orderDate.textContent = order.created.toLocaleString(DateTime.DATE_SHORT);
                // received = true;
                hasBeenReceived(order);
            }

            // Production Stages
            if (order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_CONFIRMED) {
                if (order.confirmedTime.isValid) {
                    inProduction = true;
                    hasGoneThroughProduction(order);
                }
            }

            // Order Status
            // First I check to see if the ordershippingoptionsshipments is there. This takes priority since this will mean whether it's delivered or shipped
            if (order.orderShippingOptions.length) {
                const shippingOptions = order.orderShippingOptions;

                shippingOptions.forEach(el => {
                    for (const index of el.orderShippingOptionShipments) {
                        if (index.shipment?.deliveredAt.isValid) {
                            deliveryCounter++;
                        }
                        if (index.shipment?.shippedAt.isValid) {
                            shippedCounter++;
                        }
                    }
                });

                if (deliveryCounter) {
                    delivered = true;
                    isDelivered(deliveryCounter, shippingOptions[0].orderShippingOptionShipments);
                }

                if (shippedCounter) {
                    shipped = true;
                    hasBeenShipped(shippedCounter, shippingOptions[0].orderShippingOptionShipments);
                }
            }

            // Order Number
            if (order.invoiceNumber) {
                oderNumber.textContent = `#${String(order.invoiceNumber)}`;
            }

            // Delivery shows the locality only (city, state, zip, country), never the name or street.
            if (order.orderShippingOptions.length) {
                const shippingOption = order.orderShippingOptions[0].shippingOption;
                if (shippingOption) {
                    delivery.innerHTML = html.Nl2br(html.Escape(shippingOption.address?.toLocalityLine() ?? ''));
                }
            }

            // Subtotal / shipping / tax / total
            subtotalEl.textContent = DollarFormat(order.totalSansTax.sub(order.balance));
            shipping.textContent = DollarFormat(order.balance);
            salesTax.textContent = DollarFormat(order.total.sub(order.totalSansTax));
            total.textContent = DollarFormat(order.total);

            if (order.orderProofs.length) {
                proofsParentEl.classList.remove('d-none');

                const $proofLinks: HTMLElement[] = [];

                for (const orderProof of order.orderProofs) {
                    const proof = orderProof.proof;
                    if (!proof?.main) {
                        continue;
                    }

                    const $thumbnail = document.createElement('div');
                    $thumbnail.classList.add('col', 'mb-5', 'mb-sm-none', 'me-sm-5');

                    const $imgLink = document.createElement('a');
                    $imgLink.href = proof.main;
                    $imgLink.setAttribute('data-fancybox', 'gallery');
                    $imgLink.setAttribute('data-proof-id', proof.id);

                    const $img = document.createElement('img');
                    $img.src = proof.thumbnail || proof.main;

                    $imgLink.append($img);
                    $thumbnail.append($imgLink);
                    proofsContainer.append($thumbnail);
                    $proofLinks.push($imgLink);
                }

                openLinkedProof($proofLinks);
            }

            const $invoice = document.querySelector<HTMLButtonElement>('#download-invoice');
            if ($invoice) {
                $invoice.textContent = order.invoiceNumber ?
                    `Download invoice #${order.invoiceNumber} (PDF)` :
                    'Download invoice (PDF)';
                const search = OrderRefSearch(key, 'id');
                const q = search.keyed ? search.q : `id:${order.id ?? ''}`;
                $invoice.classList.remove('d-none');
                $invoice.onclick = () => {
                    buttons.Start($invoice);
                    void DownloadOrderInvoice(q).catch(ShowError).finally(() => buttons.Stop($invoice));
                };
            }

            stopSpinner();
            reveal();
        } catch (err) {
            console.error(err);
        }
    } else {
        stopSpinner();
        noKey.classList.remove('d-none');
    }
}
